import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Option } from 'react-select';
import classnames from 'classnames';

import './style.scss';

interface DropdownProps {
  options: CategoryOptionType[];
  suggestedOptions: CategoryOptionType[];
  handleCategorySelect: (e: ICRMCategoryResponse) => (e?: React.SyntheticEvent<any>) => void;
  getSuggestedCategories: (crmToken: string, description: string) => Promise<any>;
  crmToken: string;
  error: string;
  selectedCategory?: { id?: number; name?: string; label?: string } | null;
}
const MultiSectionDropdown: React.FC<DropdownProps> = ({
  options,
  handleCategorySelect,
  suggestedOptions,
  getSuggestedCategories,
  crmToken,
  error,
  selectedCategory,
}) => {
  const filteredOptionsArray = useMemo(
    () =>
      options?.map((item) => ({
        ...item,
        name: item?.name?.trim(),
        label: item?.label?.trim(),
      })),
    [options]
  );

  useEffect(() => {
    filteredOptionsArray?.sort((item1, item2) => {
      if (item1.name.trim().toLowerCase() < item2.name.trim().toLowerCase()) {
        return -1;
      }
      if (item1.name.trim().toLowerCase() > item2.name.trim().toLowerCase()) {
        return 1;
      }

      return 0;
    });
  }, [filteredOptionsArray]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  useEffect(() => {
    if (selectedCategory === undefined) {
      return;
    }

    if (!selectedCategory) {
      setSelectedOption(null);

      return;
    }

    setSelectedOption({
      ...selectedCategory,
      label: selectedCategory.label || selectedCategory.name,
    } as Option);
  }, [selectedCategory]);
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  const menuRef = useRef(null);
  const idPrefixRef = useRef(`multi-section-dropdown-${Math.random().toString(36).slice(2, 10)}`);
  const labelId = `${idPrefixRef.current}-label`;
  const valueId = `${idPrefixRef.current}-value`;
  const listboxId = `${idPrefixRef.current}-listbox`;
  const errorId = `${idPrefixRef.current}-error`;
  const suggestedLabelId = `${idPrefixRef.current}-suggested-label`;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredOptions, setFilteredOptions] = useState<CategoryOptionType[]>(filteredOptionsArray);
  const [filteredSuggestedOptions, setFilteredSuggestedOptions] = useState<CategoryOptionType[]>(suggestedOptions);
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOptions([]);
      setFilteredSuggestedOptions([]);
    }
  }, [searchTerm]);
  useEffect(() => {
    if (searchTerm.trim()) {
      const filterOptions = (options) =>
        options?.filter((option) => option?.label?.toLowerCase()?.includes(searchTerm.toLowerCase()));
      const filteredRes = filterOptions(options);
      if (filteredRes?.findIndex((opt) => opt?.name?.toLowerCase() === DefaultCategory.OTHER) === -1) {
        const insert = options?.find((opt) => opt?.name?.toLowerCase() === DefaultCategory.OTHER);
        if (insert) filteredRes?.push(insert);
      }
      filteredRes?.sort((item1, item2) => {
        if (item1.name.trim().toLowerCase() < item2.name.trim().toLowerCase()) {
          return -1;
        }
        if (item1.name.trim().toLowerCase() > item2.name.trim().toLowerCase()) {
          return 1;
        }

        return 0;
      });
      setFilteredOptions(filteredRes);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    if (searchTerm.trim()) {
      getSuggestedCategories(crmToken, searchTerm).then((res) => {
        const filteredRes = [];
        options?.forEach((opt) => {
          if (res.response.categoryIds.includes(opt?.id)) {
            opt.color = 'grey';
            filteredRes.push(opt);
          }
        });
        filteredRes?.sort((item1, item2) => {
          if (item1.name.trim().toLowerCase() < item2.name.trim().toLowerCase()) {
            return -1;
          }
          if (item1.name.trim().toLowerCase() > item2.name.trim().toLowerCase()) {
            return 1;
          }

          return 0;
        });
        setFilteredSuggestedOptions(filteredRes);
      });
    }
  }, [searchTerm]);

  const handleOptionClick = (option: any) => {
    setSelectedOption(option);
    const { label, color, ...newObject } = option;
    const anotherObj: ICRMCategoryResponse = newObject;
    handleCategorySelect(anotherObj)(null);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCloseOnEscape = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setIsOpen(false);
    }
  };

  return (
    <div ref={menuRef} className={classnames('dropdown-wrapper', `${isOpen ? 'open' : 'hidden'}`)}>
      <div className="dropdown">
        <span id={labelId} className="h2-dropdown">
          Category
        </span>
        <button
          type="button"
          className="dropdown-btn"
          onClick={toggleDropdown}
          onKeyDown={handleCloseOnEscape}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-labelledby={`${labelId} ${valueId}`}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
        >
          <span id={valueId} className="dropdown__label">
            {selectedOption !== null ? selectedOption.label : 'Select One'}
          </span>
          <span className="dropdown__arrow_container" aria-hidden="true">
            <span className="dropdown__arrow"></span>
          </span>
        </button>
        {isOpen && (
          <div className="dropdown-content">
            <div className="dropdown-content__search-container">
              <span className="dropdown-content__search-icon" aria-hidden="true" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleCloseOnEscape}
                className="dropdown-content__search-input"
                aria-label="Search categories"
              />
            </div>
            <div className="dropdown-content__listbox" role="listbox" id={listboxId} aria-labelledby={labelId}>
              {searchTerm.trim() === '' &&
                filteredOptionsArray?.map((option, index) => (
                  <button
                    type="button"
                    key={index}
                    className="option-black"
                    role="option"
                    aria-selected={selectedOption?.id === option.id}
                    onClick={() => handleOptionClick(option)}
                    onKeyDown={handleCloseOnEscape}
                  >
                    {option?.label}
                  </button>
                ))}
              {filteredOptions?.map((option, index) => (
                <button
                  type="button"
                  key={index}
                  className="option-black"
                  role="option"
                  aria-selected={selectedOption?.id === option.id}
                  onClick={() => handleOptionClick(option)}
                  onKeyDown={handleCloseOnEscape}
                >
                  {option?.label}
                </button>
              ))}
              {filteredSuggestedOptions?.length ? (
                <div className="dropdown-content__suggested-group" role="group" aria-labelledby={suggestedLabelId}>
                  <h3 id={suggestedLabelId} className="h3-suggested-category">
                    Suggested Categories
                  </h3>
                  {filteredSuggestedOptions?.map((option, index) => (
                    <button
                      type="button"
                      key={index}
                      className="option-grey"
                      role="option"
                      aria-selected={selectedOption?.id === option.id}
                      onClick={() => handleOptionClick(option)}
                      onKeyDown={handleCloseOnEscape}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
      {!!error && (
        <p id={errorId} className="error-h2" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
export default MultiSectionDropdown;
